import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { PAYMENTS } from "@/constants/testIds";

/** Card de Ajustes: conectar/desconectar la cuenta de Square de la clínica
 * (OAuth 2.0). Mientras no este configurada la URL pública del deploy
 * (SQUARE_APP_BASE_URL), el botón de conexion real queda deshabilitado pero
 * el resto de los cobros funcionan igual con la conexion de prueba
 * (sandbox) sembrada para desarrollo. */
export default function SquareSettingsCard() {
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/square/status");
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const squareParam = searchParams.get("square");
    if (!squareParam) return;
    if (squareParam === "connected") {
      toast({ title: "Square conectado", description: "La cuenta de Square de la clínica se conecto correctamente." });
      load();
    } else if (squareParam === "error") {
      toast({ title: "No se pudo conectar con Square", description: searchParams.get("reason") || "Intenta de nuevo.", variant: "destructive" });
    }
    const next = new URLSearchParams(searchParams);
    next.delete("square");
    next.delete("reason");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/square/oauth/authorize");
      window.location.href = data.authorize_url;
    } catch (err) {
      toast({ title: "No se pudo iniciar la conexion", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await api.post("/square/disconnect");
      toast({ title: "Square desconectado" });
      load();
    } catch (err) {
      toast({ title: "Error al desconectar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setDisconnecting(false);
    }
  };

  if (!status) return null;

  return (
    <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-4" data-testid={PAYMENTS.squareSettingsCard}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-brand" />
          <h2 className="font-serif text-xl text-foreground">Pagos con Square</h2>
        </div>
        {status.connected ? (
          <Badge className="bg-[#E3EDE1] text-[#5B7A54] border-none gap-1" data-testid={PAYMENTS.squareConnectedBadge}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Conectado
          </Badge>
        ) : (
          <Badge className="bg-[#F4E1E1] text-[#A34B49] border-none gap-1" data-testid={PAYMENTS.squareNotConnectedBadge}>
            <XCircle className="w-3.5 h-3.5" /> No conectado
          </Badge>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        Conecta la cuenta de Square de la clínica para generar links de cobro, cobrar con tarjeta y registrar los pagos automaticamente en la ficha del paciente.
      </p>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Entorno:</span>
        <Badge variant="outline" className="capitalize" data-testid={PAYMENTS.squareEnvironmentBadge}>{status.environment}</Badge>
        {status.is_dev_seeded && <span className="text-muted-foreground">(conexion de prueba para desarrollo)</span>}
      </div>

      {status.connected && (
        <div className="text-xs text-muted-foreground space-y-1">
          {status.merchant_id && <p>Merchant ID: <span className="font-mono">{status.merchant_id}</span></p>}
          {status.location_id && <p>Location ID: <span className="font-mono">{status.location_id}</span></p>}
        </div>
      )}

      {!status.base_url_configured && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid={PAYMENTS.squareBaseUrlWarning}>
          La URL pública de la app todavía no esta configurada en el servidor, así que la conexion real por OAuth se activara recien despues del deploy. Los cobros y links de prueba ya funcionan en Sandbox.
        </p>
      )}

      <div className="flex gap-2">
        {status.connected ? (
          <Button variant="outline" onClick={handleDisconnect} disabled={disconnecting} data-testid={PAYMENTS.squareDisconnectButton}>
            {disconnecting ? "Desconectando..." : "Desconectar"}
          </Button>
        ) : (
          <Button onClick={handleConnect} disabled={connecting} className="bg-brand hover:bg-brand-hover text-white gap-2" data-testid={PAYMENTS.squareConnectButton}>
            <ExternalLink className="w-4 h-4" /> {connecting ? "Redirigiendo..." : "Conectar con Square"}
          </Button>
        )}
      </div>
    </div>
  );
}
